import {useEffect, useState, useMemo, useRef} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useHistoryContextStore} from '../../../zustand/historyContextStore';
import {useIsSuperAdmin} from 'hooks';
import {useQuery, useMutation} from '@apollo/client';
import {useSelector} from 'react-redux';
import {
  Button,
  Input,
  Spin,
  Upload,
  message,
  Tooltip,
  Modal,
  Space,
} from 'antd';
import {
  CloseCircleFilled,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PaperClipOutlined,
  SendOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {client} from 'index';
import {formatFileName} from 'utils/helper';
import {isDev} from 'utils/constants';
import {GET_TICKET} from 'graphql/queries/aiHelpdesk';

const TICKET_FILE_BUCKET = isDev
  ? 'sassandra-artifacts-dev'
  : 'sassandra-artifacts';
const TICKET_FILE_FOLDER = 'tickets';

const uploadTicketFile = async file => {
  const safeName = formatFileName(file.name);
  const filename = `${TICKET_FILE_FOLDER}/${safeName}`;
  const {data} = await client.mutate({
    mutation: PRE_SIGN_MEDIA_URL,
    variables: {
      input: {
        bucket: TICKET_FILE_BUCKET,
        upload: true,
        filename,
        contentType: file?.type,
      },
    },
  });
  if (data?.presignMediaURL?.statusCode === 400) {
    throw new Error(data?.presignMediaURL?.message || 'Presign failed');
  }
  const presignedUrl = data?.presignMediaURL?.data?.url;
  if (!presignedUrl) throw new Error('Missing presigned URL');
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {'Content-Type': file?.type},
    body: file,
  });
  if (!res.ok) throw new Error('Upload failed');
  return {
    url: `https://${TICKET_FILE_BUCKET}.s3.us-east-1.amazonaws.com/${filename}`,
    name: file.name,
    type: file.type,
  };
};
import {
  ASSIGN_TICKET,
  ADD_TICKET_EVENT,
  UPDATE_TICKET_STATUS,
  LINK_TICKET_TO_USER,
  UNLINK_TICKET_FROM_USER,
} from 'graphql/mutations/aiHelpdesk';
import styles from './SingleTicketView.module.scss';
import {ConversationThread} from './ConversationThread';
import {ContextSidebar} from './ContextSidebar';
import {CsatStars} from '../components/CsatStars';
import {CorrectAiModal} from '../modals/CorrectAiModal';
import {EscalationModal} from '../modals/EscalationModal';
import {HandOverModal} from '../modals/HandOverModal';
import {ResolveTicketModal} from '../modals/ResolveTicketModal';
import {LinkAnonymousTicketModal} from '../modals/LinkAnonymousTicketModal';
import {UnlinkAnonymousTicketModal} from '../modals/UnlinkAnonymousTicketModal';
import {formatAgentDisplayName} from '../helpdeskTicketsUtils';
import {refreshTicketCounts} from '../TicketAlertsWatcher';
import {supportTicketReturnTo} from '../ticketQueueState';
import {toast} from 'react-toastify';

const SingleTicketView = ({ticketId, embedded = false}) => {
  const navigate = useNavigate();
  const {id: routeId} = useParams();
  const id = ticketId || routeId;
  const admin = useSelector(state => state?.auth?.userData?.user);
  const adminId = admin?.id;
  const adminName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim();

  const [messageInput, setMessageInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]); // uploaded {url,name,type}
  const [uploadingCount, setUploadingCount] = useState(0);
  const [correctModalVisible, setCorrectModalVisible] = useState(false);
  const [escalateModalVisible, setEscalateModalVisible] = useState(false);
  const [handOverModalVisible, setHandOverModalVisible] = useState(false);
  // Resolving asks for the ticket's issue first; see ResolveTicketModal.
  const [resolveOpen, setResolveOpen] = useState(false);
  const [handOverLoading, setHandOverLoading] = useState(false);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [unlinkModalVisible, setUnlinkModalVisible] = useState(false);
  const [showSidebar, setShowSidebar] = useState(
    () =>
      // In the embedded chat view the detail pane is already narrow, so start
      // with the context hidden and let the agent reveal it with the toggle.
      !embedded &&
      (typeof window === 'undefined' ||
        window.matchMedia('(min-width: 768px)').matches),
  );
  const threadEndRef = useRef(null);

  const {data, loading, error, refetch} = useQuery(GET_TICKET, {
    variables: {input: {id}},
    skip: !id,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const ticket = useMemo(() => {
    if (data?.getTicket?.__typename === 'ResponseWithHelpdeskTicket') {
      return data.getTicket.data;
    }
    return null;
  }, [data]);

  // Make an "Add to shortcuts" of a single ticket resume the exact ticket and
  // show who + which ticket + the issue (not just the generic support page).
  const location = useLocation();
  const setPageDescriptor = useHistoryContextStore(
    state => state.setPageDescriptor,
  );
  const clearPageDescriptor = useHistoryContextStore(
    state => state.clearPageDescriptor,
  );
  const isSuperAdmin = useIsSuperAdmin();

  useEffect(() => {
    if (!isSuperAdmin || embedded || !ticket?.id) return undefined;
    const customer =
      [ticket.user?.firstName, ticket.user?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim() ||
      ticket.user?.email ||
      ticket.contactEmail ||
      'Customer';
    const ticketRef = `TKT-${String(ticket.id).slice(0, 8).toUpperCase()}`;
    const issue =
      (ticket.intent && String(ticket.intent).trim()) || 'Support ticket';
    setPageDescriptor({
      path: location.pathname,
      title: issue,
      subtitle: customer,
      context: {scope: 'ticket', ticketId: ticket.id},
      meta: [
        {key: 'ticket', label: 'Ticket', value: ticketRef},
        ...(ticket.status
          ? [{key: 'status', label: 'Status', value: ticket.status}]
          : []),
      ],
    });
    return () => clearPageDescriptor();
  }, [
    isSuperAdmin,
    embedded,
    ticket?.id,
    ticket?.intent,
    ticket?.status,
    ticket?.user,
    ticket?.contactEmail,
    location.pathname,
    setPageDescriptor,
    clearPageDescriptor,
  ]);

  useEffect(() => {
    if (!ticket) return;
    if (['RESOLVED', 'ABANDONED'].includes(ticket.status)) return;

    const timer = setInterval(() => {
      refetch();
    }, 5000); // 5s polling

    return () => clearInterval(timer);
  }, [ticket, refetch]);

  const scrollToBottom = () => {
    threadEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  useEffect(() => {
    if (ticket?.events?.length) {
      scrollToBottom();
    }
  }, [ticket?.events?.length]);

  const [assignMut] = useMutation(ASSIGN_TICKET, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const [addEventMut, {loading: isSending}] = useMutation(ADD_TICKET_EVENT, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const [updateStatusMut] = useMutation(UPDATE_TICKET_STATUS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const [linkTicketMut, {loading: linkLoading}] = useMutation(
    LINK_TICKET_TO_USER,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );
  const [unlinkTicketMut, {loading: unlinkLoading}] = useMutation(
    UNLINK_TICKET_FROM_USER,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const handleLinkTicket = async ({userId, userName, note}, onDone) => {
    if (!userId) return;
    try {
      const res = await linkTicketMut({
        variables: {input: {id, userId, note}},
      });
      const result = res.data?.linkTicketToUser;
      if (result?.__typename === 'Error') {
        message.error(result.message);
      } else {
        message.success(
          userName ? `Linked ticket to ${userName}` : 'Ticket linked',
        );
        refetch();
        onDone?.();
      }
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleUnlinkTicket = async ({reason}, onDone) => {
    if (!reason) return;
    try {
      const res = await unlinkTicketMut({
        variables: {input: {id, reason}},
      });
      const result = res.data?.unlinkTicketFromUser;
      if (result?.__typename === 'Error') {
        message.error(result.message);
      } else {
        message.success('Ticket unlinked');
        refetch();
        onDone?.();
      }
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleActionWithConfirm = (type, actionLabel) => {
    Modal.confirm({
      title: `Confirm ${actionLabel}`,
      content: `Are you sure you want to ${actionLabel.toLowerCase()}?`,
      onOk: () => handleAction(type),
    });
  };

  const handleAction = async type => {
    try {
      if (type === 'ASSIGN') {
        const res = await assignMut({
          variables: {
            input: {
              id,
              assignedAgentId: adminId ? String(adminId) : null,
              assignedAgentName: adminName,
              status: 'HUMAN_ACTIVE',
            },
          },
        });
        if (res.data?.assignTicket?.__typename === 'Error') {
          message.error(res.data.assignTicket.message);
        } else {
          message.success('Ticket assigned to you');
          refetch();
        }
      } else if (type === 'RESUME_AI') {
        const res = await assignMut({
          variables: {
            input: {
              id,
              assignedAgentId: null,
              assignedAgentName: null,
              status: 'AI_ACTIVE',
            },
          },
        });
        if (res.data?.assignTicket?.__typename === 'Error') {
          message.error(res.data.assignTicket.message);
        } else {
          message.success('Handed back to AI');
          refetch();
        }
      } else if (type === 'REOPEN') {
        const res = await updateStatusMut({
          variables: {
            input: {
              id,
              status: 'ESCALATED',
            },
          },
        });
        if (res.data?.updateTicketStatus?.__typename === 'Error') {
          message.error(res.data.updateTicketStatus.message);
        } else {
          message.success('Ticket reopened');
          refetch();
        }
      }
    } catch (err) {
      message.error(err.message);
    }
  };

  // Reassign the ticket to another admin. We keep status === HUMAN_ACTIVE
  // (the ticket is still in human hands, just a different human's). The
  // assignTicket mutation is the same one used for "Take Over" — only the
  // ids change.
  const handleHandOver = async (
    {id: nextAgentId, name: nextAgentName},
    onDone,
  ) => {
    if (!nextAgentId) return;
    setHandOverLoading(true);
    try {
      const res = await assignMut({
        variables: {
          input: {
            id,
            assignedAgentId: nextAgentId,
            assignedAgentName: nextAgentName || null,
            status: 'HUMAN_ACTIVE',
          },
        },
      });
      if (res.data?.assignTicket?.__typename === 'Error') {
        message.error(res.data.assignTicket.message);
      } else {
        message.success(
          nextAgentName
            ? `Ticket handed over to ${nextAgentName}`
            : 'Ticket handed over',
        );
        refetch();
        onDone?.();
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setHandOverLoading(false);
    }
  };

  const handleAttachFiles = async fileList => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingCount(c => c + files.length);
    const uploads = await Promise.all(
      files.map(async f => {
        try {
          return await uploadTicketFile(f);
        } catch (e) {
          message.error(`${f.name}: ${e.message || 'Upload failed'}`);
          return null;
        }
      }),
    );
    const successful = uploads.filter(Boolean);
    if (successful.length) {
      setPendingFiles(prev => [...prev, ...successful]);
    }
    setUploadingCount(c => Math.max(0, c - files.length));
  };

  const removePendingFile = idx =>
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSendMessage = async () => {
    const text = messageInput.trim();
    if (!text && !pendingFiles.length) return;
    try {
      const res = await addEventMut({
        variables: {
          input: {
            ticketId: id,
            eventType: 'AGENT_MESSAGE',
            payload: {
              text,
              agent_name: adminName,
              ...(pendingFiles.length ? {files: pendingFiles} : {}),
            },
          },
        },
      });
      const result = res.data?.addTicketEvent;
      if (result?.__typename === 'Error') {
        if (result.statusCode === 403) {
          message.error('Take over the ticket first.');
          refetch();
        } else {
          message.error(result.message);
        }
      } else {
        setMessageInput('');
        setPendingFiles([]);
        refetch();
        // The reply clears awaitingAgentSince: drop the badge and the
        // "Replied" pill now (the list sits beside us in the chat view).
        refreshTicketCounts({lists: true});
      }
    } catch (err) {
      if (/forbidden|take over|not assigned/i.test(err.message || '')) {
        message.error('Take over the ticket first.');
        refetch();
      } else {
        message.error(err.message);
      }
    }
  };

  const ticketStatus = ticket?.status || 'GUIDED_FLOW';

  const getActionBar = () => {
    if (!ticket) return null;

    switch (ticketStatus) {
      case 'GUIDED_FLOW':
        // The user is still walking through the guided picker — Sassandra
        // hasn't even started replying. Agent take-over here would yank
        // them out of the flow mid-question. Wait for escalation.
        return (
          <div className={styles.actionInfo}>
            The customer is still completing the guided flow. Wait until the
            ticket escalates before taking over.
          </div>
        );
      case 'AI_ACTIVE':
        // Sassandra is actively replying. Same rule as the spec calls for —
        // only escalated tickets can be taken over; here the agent should
        // let the AI finish or escalate first.
        return (
          <div className={styles.actionInfo}>
            Sassandra is handling this conversation. Take-over is only available
            once the ticket has been escalated.
          </div>
        );
      case 'HUMAN_ACTIVE': {
        const isAssignedToMe =
          ticket.assignedAgentId &&
          adminId &&
          String(ticket.assignedAgentId) === String(adminId);

        if (!isAssignedToMe) {
          const currentAssigneeName =
            formatAgentDisplayName(
              ticket.assignedAgent,
              ticket.assignedAgentName,
            ) || 'another agent';
          return (
            <div className={styles.assignedElsewhere}>
              <span className={styles.assignedNote}>
                Assigned to <strong>{currentAssigneeName}</strong>
              </span>
              <Space>
                <Button onClick={() => setHandOverModalVisible(true)}>
                  Reassign to another admin
                </Button>
                <Button
                  type="primary"
                  onClick={() =>
                    handleActionWithConfirm('ASSIGN', 'Take Over')
                  }>
                  Take Over
                </Button>
              </Space>
            </div>
          );
        }

        return (
          <div className={styles.messageComposition}>
            {pendingFiles.length || uploadingCount ? (
              <div className={styles.pendingFiles}>
                {pendingFiles.map((f, i) => {
                  const isImage = f.type?.startsWith('image/');
                  const isVideo = f.type?.startsWith('video/');
                  return (
                    <div
                      className={styles.pendingFileChip}
                      key={`${f.url}-${i}`}>
                      {isImage ? (
                        <img
                          src={f.url}
                          alt={f.name}
                          className={styles.pendingFileThumb}
                        />
                      ) : isVideo ? (
                        <video
                          src={f.url}
                          className={styles.pendingFileThumb}
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <FileTextOutlined className={styles.pendingFileIcon} />
                      )}
                      <span className={styles.pendingFileName} title={f.name}>
                        {f.name}
                      </span>
                      <button
                        type="button"
                        className={styles.pendingFileRemove}
                        onClick={() => removePendingFile(i)}
                        aria-label={`Remove ${f.name}`}>
                        <CloseCircleFilled />
                      </button>
                    </div>
                  );
                })}
                {uploadingCount > 0 ? (
                  <div className={styles.pendingFileChip}>
                    <Spin size="small" />
                    <span className={styles.pendingFileName}>
                      Uploading {uploadingCount}…
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className={styles.inputArea}>
              <Upload
                multiple
                showUploadList={false}
                accept="image/*,video/*,.pdf,.doc,.docx,.csv,.txt,.zip"
                beforeUpload={(file, fileList) => {
                  // beforeUpload fires once per file; batch on the last call.
                  if (fileList[fileList.length - 1] === file) {
                    handleAttachFiles(fileList);
                  }
                  return false;
                }}>
                <Tooltip title="Attach files">
                  <Button
                    type="text"
                    icon={<PaperClipOutlined />}
                    className={styles.attachBtn}
                    disabled={uploadingCount > 0}
                  />
                </Tooltip>
              </Upload>
              <Input.TextArea
                placeholder="Type your message..."
                autoSize={{minRows: 1, maxRows: 6}}
                value={messageInput}
                onChange={e => setMessageInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                className={styles.textArea}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={isSending}
                disabled={
                  (!messageInput.trim() && !pendingFiles.length) ||
                  isSending ||
                  uploadingCount > 0
                }
                className={styles.sendBtn}
              />
            </div>
            <div className={styles.secondaryActions}>
              <div className={styles.leftGroup}>
                {/* <Button
                  className={styles.resumeBtn}
                  icon={<UndoOutlined />}
                  onClick={() =>
                    handleActionWithConfirm('RESUME_AI', 'Hand back to AI')
                  }>
                  Hand back to AI
                </Button>
                <Button
                  danger
                  className={styles.escalateBtn}
                  icon={<ExclamationCircleOutlined />}
                  onClick={() => setEscalateModalVisible(true)}>
                  Escalate
                </Button> */}
              </div>
              <div className={styles.rightGroup}>
                <Button
                  className={styles.handOverBtn}
                  onClick={() => setHandOverModalVisible(true)}>
                  Hand Over
                </Button>
                <Button
                  type="primary"
                  className={styles.resolveBtn}
                  icon={<CheckCircleOutlined />}
                  onClick={() => setResolveOpen(true)}>
                  Mark Resolved
                </Button>
              </div>
            </div>
          </div>
        );
      }
      case 'ESCALATED':
        return (
          <Space>
            <Button
              type="primary"
              onClick={() => handleActionWithConfirm('ASSIGN', 'Assign to Me')}>
              Assign to Me
            </Button>
            <Button type="dashed" onClick={() => setResolveOpen(true)}>
              Mark Resolved
            </Button>
          </Space>
        );
      case 'RESOLVED':
        return (
          <Space>
            <Button
              type="primary"
              onClick={() =>
                handleActionWithConfirm('REOPEN', 'Reopen Ticket')
              }>
              Reopen Ticket
            </Button>
          </Space>
        );
      default:
        return null;
    }
  };

  if (loading && !data) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Spin size="large" tip="Loading ticket..." />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div style={{padding: '40px', textAlign: 'center'}}>
        <h3>Error loading ticket</h3>
        <p>{error?.message || 'Ticket not found'}</p>
        {!embedded && (
          <Button
            onClick={() =>
              navigate(supportTicketReturnTo(location), {replace: true})
            }>
            Back to Tickets
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${
        embedded ? styles.embeddedContainer : ''
      }`}>
      <div className={styles.header}>
        {!embedded && (
          <button
            className={styles.backBtn}
            onClick={() =>
              navigate(supportTicketReturnTo(location), {replace: true})
            }>
            &larr; Back to Tickets
          </button>
        )}
        <div className={styles.headerInfo}>
          <div className={styles.ticketTitleWrapper}>
            <span className={styles.ticketLabel}>TICKET</span>
            <span className={styles.ticketId}>
              TKT-{ticket.id.slice(0, 8).toUpperCase()}
            </span>
          </div>
          <span className={`${styles.statusBadge} ${styles[ticketStatus]}`}>
            {ticketStatus.replace('_', ' ')}
          </span>
          {ticket.origin === 'ANONYMOUS' ? (
            <span
              className={styles.anonymousChip}
              title={
                ticket.userId
                  ? 'Originally anonymous — now linked to an account'
                  : 'Anonymous ticket — no account linked yet'
              }>
              {ticket.userId ? 'Anon · Linked' : 'Anonymous'}
              {ticket.userId ? (
                <Button
                  size="small"
                  type="link"
                  onClick={() => setUnlinkModalVisible(true)}>
                  Unlink
                </Button>
              ) : (
                <Button
                  size="small"
                  type="link"
                  onClick={() => setLinkModalVisible(true)}>
                  Link to user
                </Button>
              )}
            </span>
          ) : null}
          {ticketStatus === 'RESOLVED' ||
          (ticket.csatScore !== null && ticket.csatScore !== undefined) ? (
            <span
              style={{display: 'inline-flex', alignItems: 'center', gap: 6}}
              title="Customer satisfaction (set by the customer; never the admin)">
              <CsatStars score={ticket.csatScore ?? null} size="md" />
            </span>
          ) : null}
          {ticket.resolvingFaqPostId ? (
            <span className={styles.faqBadge}>
              Resolved via FAQ #{ticket.resolvingFaqPostId}
            </span>
          ) : null}
          {Array.isArray(ticket.skillsUsed) && ticket.skillsUsed.length ? (
            <span className={styles.skillsChip}>
              Used skills:{' '}
              {ticket.skillsUsed
                .map(
                  s =>
                    [s?.skill?.node, s?.skill?.tree]
                      .filter(Boolean)
                      .join(' / ') || s?.skillId,
                )
                .filter(Boolean)
                .join(', ')}
            </span>
          ) : null}
        </div>
        <div style={{flex: 1}} />
        <Tooltip title={showSidebar ? 'Hide Context' : 'Show Context'}>
          <Button
            type="text"
            icon={showSidebar ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={() => setShowSidebar(!showSidebar)}
            className={styles.sidebarToggle}
          />
        </Tooltip>
      </div>
      <div
        className={`${styles.splitView} ${
          !showSidebar ? styles.sidebarHidden : ''
        }`}>
        {/* Embedded (chat) view switches between chat and context so neither
            gets squeezed; the standalone page keeps them side by side. */}
        {!(embedded && showSidebar) && (
          <div className={styles.leftPanel}>
            <div className={styles.thread}>
              <ConversationThread events={ticket.events} />
              <div ref={threadEndRef} />
            </div>
            <div className={styles.actionBar}>{getActionBar()}</div>
          </div>
        )}
        {showSidebar && (
          <div
            className={`${styles.rightPanel} ${
              embedded ? styles.rightPanelFull : ''
            }`}>
            <ContextSidebar ticket={ticket} />
          </div>
        )}
      </div>

      <CorrectAiModal
        visible={correctModalVisible}
        onClose={() => setCorrectModalVisible(false)}
        originalResponse="I see your deposit for 5,000 NGN via Orange Money..."
      />

      <EscalationModal
        visible={escalateModalVisible}
        onClose={() => setEscalateModalVisible(false)}
        onSubmit={() => refetch()}
      />

      <LinkAnonymousTicketModal
        isOpen={linkModalVisible}
        onClose={() => setLinkModalVisible(false)}
        onLink={handleLinkTicket}
        loading={linkLoading}
        contactEmail={ticket?.contactEmail || null}
      />

      <UnlinkAnonymousTicketModal
        isOpen={unlinkModalVisible}
        onClose={() => setUnlinkModalVisible(false)}
        onUnlink={handleUnlinkTicket}
        loading={unlinkLoading}
        linkedUserDisplay={
          formatAgentDisplayName(ticket?.user, null) ||
          ticket?.user?.email ||
          null
        }
      />

      <ResolveTicketModal
        ticket={ticket}
        isOpen={resolveOpen}
        onClose={() => setResolveOpen(false)}
        onResolved={refetch}
      />

      <HandOverModal
        isOpen={handOverModalVisible}
        onClose={() => setHandOverModalVisible(false)}
        onHandOver={handleHandOver}
        loading={handOverLoading}
        currentAssigneeId={ticket?.assignedAgentId || null}
        currentAssigneeName={
          formatAgentDisplayName(
            ticket?.assignedAgent,
            ticket?.assignedAgentName,
          ) || null
        }
        title={
          ticket?.assignedAgentId &&
          adminId &&
          String(ticket.assignedAgentId) === String(adminId)
            ? 'Hand Over Ticket'
            : 'Reassign Ticket'
        }
        submitLabel={
          ticket?.assignedAgentId &&
          adminId &&
          String(ticket.assignedAgentId) === String(adminId)
            ? 'Hand Over'
            : 'Reassign'
        }
      />
    </div>
  );
};

export default SingleTicketView;
